import React, { useEffect, useState } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  AreaChart,
  Area,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Label,
  Dot,
  ComposedChart,
  Bar,
} from 'recharts';

import {
  Grid,
  FormControlLabel,
  Typography,
  Card,
  CardContent,
  Checkbox,
  Switch,
  Button,
  Collapse,
  Alert,
} from '@mui/material';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import LockOpenRoundedIcon from '@mui/icons-material/LockOpenRounded';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';

import { Brush } from 'src/App/Components';
import { COLOR_PRIMARY, COLOR_PRIMARY_DARK, COLOR_PRIMARY_LIGHT, exchangeRates } from 'src/const';
import { RechartsDataFormat } from 'src/interfaces/idashboard';
import { RateAnalysisCardPropTypes } from 'src/interfaces/iproptypes';
import {
  abbreviateNumber,
  calculateMoMChange,
  calculateRollingYoYChange,
  convertCurrency,
  findMaxDay,
  getDayFromDate,
  getMoMYoYChangeColor,
} from 'src/utils/functions';
import {
  COLOR_BACKGROUND_1,
  COLOR_ERROR_1,
  COLOR_ERROR_BACKGROUNG,
  COLOR_GREY_600,
  COLOR_SUCCESS_1,
  COLOR_SUCCESS_BACKGROUNG,
  COLOR_TEXT_1,
  COLOR_TEXT_2,
  COLOR_WARNING,
  HISTORICAL_BARGRAPH_COLOR,
} from 'src/colors';
import { SelecFilterChip } from 'src/App/Components/Chips';
import UnlockCTAButton from 'src/App/Components/Buttons/UnlockCTAButton';
import { useSelector } from 'react-redux';

const RateAnalysisCard = ({
  title,
  data,
  monthlyData,
  metaData,
  currency,
  dateList,
  getMedianData,
  medianKeys,
  medianColors,
  titleMedianSwitch,
  TooltipBody,
  isFilterOpen,
  hideMedianToggleSwitch,
  granularityType,
  showNoPercentile,
  isCurrency,
  activeDaysData,
  isDisabled,
  isMobileScreen,
  handleOnSampleClick,
  barColor,
}: RateAnalysisCardPropTypes) => {
  const dataPoints = Object.keys(metaData);
  const [currentData, setCurrentData] = useState<any>(data);
  const [selectedFilters, setselectedFilters] = useState<string[]>([
    ...dataPoints.filter(dataPoint => dataPoint.includes('50')),
  ]);
  // const [selectedFilters, setselectedFilters] = useState<string[]>(dataPoints);
  const [filterExpanded, setFilterExpanded] = useState<boolean>(isFilterOpen ? true : false);
  const [showMedian, setShowMedian] = useState<boolean>(false);
  const [showYourBookings, setShowYourBookings] = useState<boolean>(false);
  const { medianData, medianDataRange } = getMedianData(metaData, selectedFilters, dateList);
  const { averageDataSet, averageDataRange } = getAverageData(data, selectedFilters);
  const [momChange, setMom] = useState<number>(0);
  const [yoyChange, setYoy] = useState<number>(0);
  const [bestDay, setBestDay] = useState<String>('');
  const [currentDataCopy, setCurrentDataCopy] = useState<any>(currentData);
  const theme = useTheme();
  const [activeDays, setactiveDays] = useState<any>(activeDaysData);
  const currentMarketName = useSelector(state => (state as any).dashboard.search.data.name);
  let checkMaxMinMarkerVisible = {
    key: '',
    max: false,
    min: false,
  };

  useEffect(() => {
    // using only median MoM and YoY change
    const metricsToCalculate = showNoPercentile ? dataPoints[0] : dataPoints[2];
    const mom = calculateMoMChange(monthlyData, metricsToCalculate);
    const yoy = calculateRollingYoYChange(monthlyData, metricsToCalculate);
    setMom(mom as number);
    setYoy(yoy as number);
    setBestDay(getBestDay(data, metricsToCalculate));
  }, [data]);

  const getWhichPercentile = metric => {
    if (metric.includes('25')) {
      return '25th percentile';
    } else if (metric.includes('50')) {
      return '50th percentile';
    } else if (metric.includes('75')) {
      return '75th percentile';
    } else if (metric.includes('90')) {
      return '90th percentile';
    } else {
      return '';
    }
  };

  const getDataAccordingToDateAndMetrics = (data, date, metrics) => {
    const individualData = data.find(dataItem => dataItem.date === date);
    if (individualData && individualData[metrics]) {
      return individualData[metrics];
    }
    return null;
  };

  const getBestDay = (data, metricKey): string => {
    let days = {
      Monday: [],
      Tuesday: [],
      Wednesday: [],
      Thursday: [],
      Friday: [],
      Saturday: [],
      Sunday: [],
    } as { [day: string]: number[] };

    data.forEach(dataPerDay => {
      let day = getDayFromDate(new Date(dataPerDay.date));
      if (dataPerDay[metricKey]) {
        days[day].push(dataPerDay[metricKey] as number);
      }
    });

    let daysWithAvgVal = {} as { [key: string]: number };
    for (let day in days) {
      const vals = days[day];
      const avgVal = vals.reduce((partialSum, a) => partialSum + a, 0) / vals.length;
      daysWithAvgVal[day] = avgVal;
    }
    return findMaxDay(daysWithAvgVal);
  };

  const convertGraphCurrency = (data, isCurrency) => {
    if (isCurrency) {
      const currencyConvetedData = data.map(currentData => {
        const newGranularObj = {};
        Object.keys(currentData).forEach(key => {
          newGranularObj[key] =
            currentData[key] && key !== 'date' && key !== 'endDate'
              ? convertCurrency(currency, Number(currentData[key]), true)
              : currentData[key];
        });
        return newGranularObj;
      });
      // console.log("==**==**== ", currencyConvetedData);
      return currencyConvetedData;
    }

    return data;
  };
  useEffect(() => {
    setCurrentData(convertGraphCurrency(data, isCurrency));
    setCurrentDataCopy(currentData);
    setactiveDays(activeDaysData);
  }, [currency, data, granularityType]);

  const CustomToolTipForLineAndBarChartCombined = ({
    date,
    occupancyData,
    activeDaysCount,
    dataPoints,
  }) => {
    return (
      <Card sx={{ borderRadius: 3, boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.25)' }}>
        <CardContent
          sx={{
            background: 'white',
            width: '300px',
            // height: '100px',
            overflowX: 'hidden',
          }}
        >
          <Typography
            variant="h6"
            sx={{ marginBottom: 2, fontWeight: 'bold', color: COLOR_PRIMARY_DARK }}
          >
            {formatDate(date, true)}
          </Typography>
          {dataPoints &&
            dataPoints.map(dataPoint => {
              if (dataPoint !== 'occupancy_rate') {
                return (
                  <Typography
                    variant="body1"
                    sx={{ marginBottom: 2, fontWeight: 'bold', color: COLOR_TEXT_2 }}
                  >
                    <>
                      {'Occupancy Rate ('}
                      {getWhichPercentile(dataPoint)}
                      {') :'}
                      <span style={{ color: COLOR_PRIMARY_DARK }}>
                        {getDataAccordingToDateAndMetrics(occupancyData, date, dataPoint)
                          ? getDataAccordingToDateAndMetrics(occupancyData, date, dataPoint)
                          : ''}
                      </span>
                      {'%'}
                    </>
                  </Typography>
                );
              } else {
                return <></>;
              }
            })}
          <Typography
            variant="body1"
            sx={{ marginBottom: 2, fontWeight: 'bold', color: COLOR_TEXT_2 }}
          >
            <>
              <span style={{ color: COLOR_SUCCESS_1 }}>
                Number of Active Listings : {activeDaysCount}
              </span>
            </>
          </Typography>
        </CardContent>
      </Card>
    );
  };

  const CustomTooltip = ({
    active,
    payload,
    label,
    occupancyData = [],
    dataPoints = [] as string[],
    granularityType: string,
  }) => {
    if (active && payload && payload[0] && payload[0].payload) {
      const date = payload[0].payload.date;

      // if (payload[0].name === 'active_listings_count') {
      //   return (
      //     <CustomToolTipForLineAndBarChartCombined
      //       date={date}
      //       occupancyData={occupancyData}
      //       activeDaysCount={payload[0].payload.active_listings_count}
      //       dataPoints={dataPoints}
      //     />
      //   );
      // }

      return (
        <TooltipBody
          date={date as Date}
          payload={payload}
          formatDate={formatDate}
          showMedian={granularityType ? false : showMedian}
          selectedFilters={granularityType ? [] : selectedFilters}
          granularityType={granularityType}
        />
      );
    }
    return null;
  };

  const changeDataAccordingToBrush = e => {
    if (e.startIndex === e.endIndex) {
      e.startIndex = e.endIndex - 1;
    }
    setactiveDays(activeDaysData?.slice(e.startIndex, e.endIndex + 1));
    setCurrentDataCopy(currentData.slice(e.startIndex, e.endIndex + 1));
  };

  const MaxMinDots = props => {
    const { cx, cy, payload, dataKey: key, maxDataPoint, minDataPoint } = props;
    const isMax = Math.round(payload[key]) == Math.round(maxDataPoint);
    const isMin = Math.round(payload[key]) == Math.round(minDataPoint);
    if (
      (isMax && checkMaxMinMarkerVisible.max == false) ||
      (isMin && checkMaxMinMarkerVisible.min == false)
    ) {
      if (isMax && checkMaxMinMarkerVisible.max == false) {
        checkMaxMinMarkerVisible.max = true;
      }
      if (isMin && checkMaxMinMarkerVisible.min == false) {
        checkMaxMinMarkerVisible.min = true;
      }
      return (
        <>
          <g>
            {isMax && (
              <Dot cx={cx} cy={cy} r={4} stroke={metaData[key].color} fill={metaData[key].color} />
            )}
            {isMin && (
              <Dot cx={cx} cy={cy} r={4} stroke={metaData[key].color} fill={metaData[key].color} />
            )}
            <text x={cx - 15} y={cy - 8} fill={COLOR_TEXT_1} fontFamily="roboto">
              {key.includes('occupancy')
                ? payload[key] + '%'
                : key.includes('lead')
                ? payload[key] + ' Days'
                : exchangeRates[currency].symbol + payload[key]}
            </text>
          </g>
        </>
      );
    } else {
      return null;
    }
  };

  return (
    <Card
      sx={{
        // width: isScreenBreaking ? '1100px' : '100%',
        width: '100%',
        overflow: 'visible',
        overflowX: 'scroll',
        borderRadius: 2,
      }}
    >
      <CardContent>
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ marginBottom: 2 }}
        >
          <Grid item xs={6}>
            <Typography variant="body1" className='subHeadings'>{title}</Typography>
          </Grid>

          <Grid item container xs={6}  justifyContent={'flex-end'}>
            {granularityType && granularityType !== 'Daily' && (
              <SelecFilterChip
                height="20px"
                width={'160px'}
                borderRadius="30px"
                fontSize={'12px'}
                colors={{
                  backgroundColor:
                    momChange > 0 ? COLOR_SUCCESS_BACKGROUNG : COLOR_ERROR_BACKGROUNG,
                  color: momChange > 0 ? COLOR_SUCCESS_1 : COLOR_ERROR_1,
                  borderColor: momChange > 0 ? COLOR_SUCCESS_BACKGROUNG : COLOR_ERROR_BACKGROUNG,
                }}
                text={`MoM Change: ${
                  momChange > 0 ? `+${momChange}%↑` : `${momChange}%${momChange >= 0 ? '↑' : '↓'}`
                }`}
                fontWeight="bold"
                handleOnClick={() => {}}
                isSelected={false}
                tooltip=""
              />
            )}
          </Grid>

          {/* {granularityType && (
            <Grid
              container
              direction="row"
              alignItems="center"
              justifyContent={'space-between'}
              style={{ fontFamily: 'Roboto', marginTop: '10px' }}
            >
              {isDisabled && (
                <Grid sm={6} xs={12} sx={{ width: '100%', height: '40px' }}>
                  <Alert
                    severity="warning"
                    sx={{
                      marginRight: '10px',
                      border: `1px solid ${COLOR_WARNING}77`,
                      fontWeight: 'bold',
                      padding: '0px 6px',
                      fontSize: '14px',
                      borderRadius: 900,
                      // boxShadow: '0 4px 8px #ccc',
                    }}
                  >
                    Showing the demo market: The City of Brighton and Hove
                  </Alert>
                </Grid>
              )}
              {isDisabled && (
                <Grid
                  item
                  sm={6}
                  xs={12}
                  sx={{
                    [theme.breakpoints.down('xs')]: {
                      marginTop: '20px',
                    },
                  }}
                >
                  <UnlockCTAButton
                    padding={'8px 20px'}
                    handleClick={() => {
                      handleOnSampleClick && handleOnSampleClick();
                    }}
                    text={`Unlock to access Historical Data for ${currentMarketName}`}
                    icon={<LockOpenRoundedIcon />}
                  />
                </Grid>
              )}
            </Grid>
          )} */}

          <Grid item xs={4} container direction="row" justifyContent="flex-end" alignItems="center">
            <Typography variant="caption" sx={{ color: COLOR_PRIMARY }}>
              {titleMedianSwitch}
            </Typography>
            {!hideMedianToggleSwitch && (
              <Switch checked={showMedian} onChange={() => setShowMedian(!showMedian)} />
            )}
            {title.includes('Competitor Price Trends') && (
              <>
                <Typography variant="caption" sx={{ color: COLOR_PRIMARY }}>
                  Your Booking Price
                </Typography>
                <Switch
                  checked={showYourBookings}
                  onChange={() => setShowYourBookings(!showYourBookings)}
                />
              </>
            )}
          </Grid>
        </Grid>

        <Grid container direction="row" justifyContent="flex-end" sx={{ marginBottom: 3 }}>
          {/* <Grid item xs={10}> */}
          {/* {granularityType && granularityType !== 'Daily' && (
              <Grid item container sm={6} xs={12}>
                <Grid item>
                  {/* <SelecFilterChip
                    height="30px"
                    width={!isMobileScreen ? '200px' : '140px'}
                    borderRadius="30px"
                    colors={{
                      backgroundColor:
                        momChange > 0 ? COLOR_SUCCESS_BACKGROUNG : COLOR_ERROR_BACKGROUNG,
                      color: momChange > 0 ? COLOR_SUCCESS_1 : COLOR_ERROR_1,
                      borderColor:
                        momChange > 0 ? COLOR_SUCCESS_BACKGROUNG : COLOR_ERROR_BACKGROUNG,
                    }}
                    text={`MoM Change: ${
                      momChange > 0
                        ? `+${momChange}%↑`
                        : `${momChange}%${momChange >= 0 ? '↑' : '↓'}`
                    }`}
                    fontWeight="bold"
                    handleOnClick={() => {}}
                    isSelected={false}
                    tooltip=""
                  /> */}
          {/* </Grid> */}
          {/* <Grid item style={{ marginLeft: '10px' }}> */}
          {/* <SelecFilterChip
                    height="30px"
                    width={!isMobileScreen ? '200px' : '140px'}
                    borderRadius="30px"
                    colors={{
                      backgroundColor:
                        yoyChange > 0 ? COLOR_SUCCESS_BACKGROUNG : COLOR_ERROR_BACKGROUNG,
                      color: yoyChange > 0 ? COLOR_SUCCESS_1 : COLOR_ERROR_1,
                      borderColor:
                        yoyChange > 0 ? COLOR_SUCCESS_BACKGROUNG : COLOR_ERROR_BACKGROUNG,
                    }}
                    text={`YoY Change: ${
                      yoyChange > 0
                        ? `+${yoyChange}%↑`
                        : `${yoyChange}%${yoyChange >= 0 ? '↑' : '↓'}`
                    }`}
                    fontWeight="bold"
                    handleOnClick={() => {}}
                    isSelected={false}
                    tooltip=""
                  /> 
                </Grid>
              </Grid>
            )} */}

          {/* {granularityType && granularityType === 'Daily' && (
              <Grid
                container
                direction="row"
                alignItems="center"
                style={{ fontFamily: 'Roboto', marginTop: '0px' }}
              >
                <Grid item>
                  <SelecFilterChip
                    height="30px"
                    width="200px"
                    borderRadius="30px"
                    colors={{
                      backgroundColor: COLOR_SUCCESS_BACKGROUNG,
                      color: COLOR_SUCCESS_1,
                      borderColor: COLOR_SUCCESS_BACKGROUNG,
                    }}
                    text={`Best Day: ${bestDay}`}
                    fontWeight="bold"
                    handleOnClick={() => {}}
                    isSelected={false}
                    tooltip=""
                  />
                </Grid>
              </Grid>
            )} */}
          {/* </Grid> */}
          {/* <Grid item xs={2}>
            <Button
              sx={{
                color: '#535353',
                width: '100%',
                fontSize: 14,
                textTransform: 'none',
                border: '1px solid #ccc',
              }}
              onClick={() => {
                setFilterExpanded(!filterExpanded);
              }}
            >
              Filters
              {filterExpanded ? (
                <KeyboardDoubleArrowUpIcon sx={{ fontSize: 14 }} />
              ) : (
                <KeyboardDoubleArrowDownIcon sx={{ fontSize: 14 }} />
              )}
            </Button>
          </Grid> */}
        </Grid>

        {/* <Collapse in={filterExpanded}> */}
        {!showNoPercentile && (
          <Grid container item direction="row" spacing={1} xs={12} sx={{ marginBottom: 5 }}>
            {Object.keys(metaData).map(key => {
              if (metaData[key].name === 'avg') {
                return;
              }
              return (
                <Grid item xs={3} key={key} style={{ display: showNoPercentile ? 'none' : 'flex' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        // defaultChecked
                        checked={selectedFilters.includes(key)}
                        sx={{
                          color: metaData[key].color,
                          '&.Mui-checked': {
                            color: metaData[key].color,
                          },
                        }}
                        onChange={() => {
                          if (selectedFilters.includes(key)) {
                            setselectedFilters(selectedFilters.filter(item => item !== key));
                          } else {
                            setselectedFilters([...selectedFilters, key]);
                          }
                        }}
                      />
                    }
                    label={metaData[key].name}
                    labelPlacement="end"
                    sx={{
                      color: COLOR_GREY_600,
                      '& .MuiTypography-root': {
                        fontSize: 16,
                      },
                    }}
                  />
                </Grid>
              );
            })}
          </Grid>
        )}
        {/* </Collapse> */}

        {currentData && (
          <Grid item xs={12}>
            {title.includes('Competitor Price Trends') && (
              <ResponsiveContainer width="100%" height={450}>
                <ComposedChart
                  height={300}
                  data={currentData}
                  margin={{ top: 20, right: 30, bottom: 20, left: 30 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis
                    dataKey="date"
                    style={{ fontFamily: 'Roboto', fontSize: isMobileScreen ? '10px' : '15px' }}
                    interval={'equidistantPreserveStart'}
                    tickCount={12}
                    padding="gap"
                    tickFormatter={tick => {
                      if (granularityType) {
                        return formatDate(tick, true, true);
                      } else {
                        return formatDate(tick);
                      }
                    }}
                    height={80}
                    angle={-90}
                    textAnchor="end"
                  />
                  <YAxis style={{ fontFamily: 'Roboto' }} tickLine={false} yAxisId="left">
                    <Label
                      value={
                        granularityType
                          ? `${title} ${isCurrency ? `(${currency})` : ''}`
                          : `Rate (${currency})`
                      }
                      angle={-90}
                      position="insideLeft"
                      style={{ textAnchor: 'middle', fontFamily: 'Roboto', fontSize: '12px' }}
                    />
                  </YAxis>

                  {Object.keys(currentData[0])
                    .reverse()
                    .map((key, idx) => {
                      if (key != 'date' && selectedFilters.includes(key)) {
                        return (
                          <Area
                            key={key}
                            type="monotone"
                            dataKey={key}
                            stroke={metaData[key].color}
                            strokeWidth={2}
                            fill={metaData[key].color}
                            isAnimationActive={false}
                            yAxisId="left"
                          />
                        );
                      } else return null;
                    })}

                  {showMedian && (
                    <Line
                      dataKey="median_booked_rate"
                      stroke="#454B1B"
                      type="monotone"
                      strokeWidth={2}
                      fill="#454B1B"
                      // isAnimationActive={false}
                      yAxisId="left"
                      dot={false}
                    />
                  )}

                  {showYourBookings && (
                    <Line
                      dataKey="user_listing_rate"
                      stroke="#0ff"
                      type="monotone"
                      strokeWidth={2}
                      fill="#0ff"
                      // isAnimationActive={false}
                      yAxisId="left"
                      dot={false}
                    />
                  )}

                  <Tooltip
                    position={{ y: -60 }}
                    content={
                      <CustomTooltip
                        active
                        payload
                        label
                        granularityType
                        occupancyData={currentData}
                        dataPoints={selectedFilters}
                      />
                    } //error
                    wrapperStyle={{ zIndex: 100000 }}
                  />
                  {/* <Brush
                    data={currentData}
                    dataKey="date"
                    height={20}
                    y={425}
                    stroke={`${COLOR_PRIMARY_LIGHT}10`}
                    travellerStroke={COLOR_PRIMARY}
                    fill="transparent"
                    onChange={e => {
                      changeDataAccordingToBrush(e);
                    }}
                    barGraphData={averageDataSet}
                    barGraphDataRange={averageDataRange}
                    barKey="average"
                    // granularityType={granularityType ? granularityType : null}
                  /> */}
                </ComposedChart>
              </ResponsiveContainer>
            )}

            {((!title.includes('Occupancy') && !title.includes('Active Listing Count')) ||
              (title.includes('Occupancy') && granularityType !== 'Monthly')) &&
              !title.includes('Competitor Price Trends') && (
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart
                    height={300}
                    data={showMedian ? medianData : currentData}
                    // margin={{ top: 20, right: 30, bottom: 20, left: 30 }}
                    margin={{ left: -20, top: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="date"
                      style={{ fontFamily: 'Roboto', fontSize: isMobileScreen ? '10px' : '12px' }}
                      interval={'equidistantPreserveStart'}
                      tickCount={12}
                      padding="gap"
                      tickFormatter={tick => {
                        if (granularityType) {
                          return formatDate(tick, true, true);
                        } else {
                          return formatDate(tick);
                        }
                      }}
                      tickMargin={10}
                      height={80}
                      // angle={-90}
                      textAnchor="middle"
                    />
                    <YAxis
                      style={{ fontFamily: 'Roboto' }}
                      tickLine={false}
                      // hide={true}
                      axisLine={{ stroke: '#0000' }}
                      tickFormatter={value => abbreviateNumber(value)}
                    >
                      {/* <Label
                        value={
                          granularityType
                            ? `${title} ${isCurrency ? `(${currency})` : ''}`
                            : `Rate (${currency})`
                        }
                        angle={-90}
                        position="insideLeft"
                        style={{ textAnchor: 'middle', fontFamily: 'Roboto', fontSize: '12px' }}
                      /> */}
                    </YAxis>
                    {!showMedian &&
                      Object.keys(currentData[0]).map((key, idx) => {
                        const { maxDataPoint, minDataPoint } = findMaxMin(
                          currentDataCopy,
                          key,
                          selectedFilters
                        );
                        if (checkMaxMinMarkerVisible.key !== key) {
                          checkMaxMinMarkerVisible.key = key;
                          checkMaxMinMarkerVisible.max = false;
                          checkMaxMinMarkerVisible.min = false;
                        }
                        if (key != 'date' && selectedFilters.includes(key)) {
                          return (
                            <Line
                              key={key}
                              type="monotone"
                              dataKey={key}
                              stroke={metaData[key].color}
                              strokeWidth={2}
                              fill={metaData[key].color}
                              isAnimationActive={false}
                              dot={
                                <MaxMinDots
                                  key={key}
                                  maxDataPoint={maxDataPoint}
                                  minDataPoint={minDataPoint}
                                />
                              }
                            />
                          );
                        } else return null;
                      })}
                    {showMedian &&
                      medianKeys.map(medianKey => {
                        return (
                          <Line
                            key={medianKey}
                            type="monotone"
                            dataKey={medianKey}
                            stroke={medianColors[medianKey]}
                            strokeWidth={2}
                            fill={medianColors[medianKey]}
                            activeDot={{ r: 6 }}
                            dot={false}
                          />
                        );
                      })}
                    <Tooltip
                      position={{ y: -60 }}
                      content={<CustomTooltip active payload label granularityType />}
                      wrapperStyle={{ zIndex: 100000 }}
                    />
                    {/* <Brush
                      height={20}
                      stroke={`${COLOR_PRIMARY_LIGHT}80`}
                      travellerStroke={COLOR_PRIMARY}
                      fill="transparent"
                      onChange={e => {
                        changeDataAccordingToBrush(e);
                      }}
                      barGraphData={showMedian ? medianData : averageDataSet}
                      barGraphDataRange={showMedian ? medianDataRange : averageDataRange}
                      barKey={showMedian ? 'Median Available Rate' : 'average'}
                      granularityType={granularityType ? granularityType : null}
                    /> */}
                  </LineChart>
                </ResponsiveContainer>
              )}

            {title.includes('Occupancy') && granularityType === 'Monthly' && (
              <ResponsiveContainer width="100%" height={350}>
                <ComposedChart
                  height={300}
                  data={showMedian ? medianData : currentData}
                  margin={{ left: -20, top: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="date"
                    style={{ fontFamily: 'Roboto', fontSize: isMobileScreen ? '10px' : '12px' }}
                    interval={'equidistantPreserveStart'}
                    tickCount={12}
                    padding="gap"
                    tickFormatter={tick => {
                      if (granularityType) {
                        return formatDate(tick, true, true);
                      } else {
                        return formatDate(tick);
                      }
                    }}
                    height={80}
                    tickMargin={10}
                    // angle={-90}
                    textAnchor="end"
                  />
                  <YAxis
                    style={{ fontFamily: 'Roboto' }}
                    tickLine={false}
                    yAxisId="left"
                    axisLine={{ stroke: '#0000' }}
                    tickFormatter={value => abbreviateNumber(value)}
                  >
                    <Label
                      value={
                        granularityType
                          ? `${title} ${isCurrency ? `(${currency})` : ''}`
                          : `Rate (${currency})`
                      }
                      angle={-90}
                      position="insideLeft"
                      style={{ textAnchor: 'middle', fontFamily: 'Roboto', fontSize: '12px' }}
                    />
                  </YAxis>
                  {/* <YAxis
                    style={{ fontFamily: 'Roboto' }}
                    tickLine={false}
                    yAxisId="right"
                    orientation="right"
                  >
                    <Label
                      value="Number of Active Listings"
                      angle={-90}
                      position="insideRight"
                      style={{ textAnchor: 'middle', fontFamily: 'Roboto', fontSize: '12px' }}
                    />
                  </YAxis> */}

                  {/* <Bar
                    radius={[5, 5, 0, 0]}
                    data={activeDays}
                    dataKey="active_listings_count"
                    barSize={50}
                    fill={HISTORICAL_BARGRAPH_COLOR}
                    yAxisId="right"
                  /> */}

                  {!showMedian &&
                    Object.keys(currentData[0]).map((key, idx) => {
                      const { maxDataPoint, minDataPoint } = findMaxMin(
                        currentDataCopy,
                        key,
                        selectedFilters
                      );

                      if (checkMaxMinMarkerVisible.key !== key) {
                        checkMaxMinMarkerVisible.key = key;
                        checkMaxMinMarkerVisible.max = false;
                        checkMaxMinMarkerVisible.min = false;
                      }
                      if (key != 'date' && selectedFilters.includes(key)) {
                        return (
                          <Line
                            key={key}
                            type="monotone"
                            dataKey={key}
                            stroke={metaData[key].color}
                            strokeWidth={2}
                            fill={metaData[key].color}
                            isAnimationActive={false}
                            yAxisId="left"
                            dot={
                              <MaxMinDots
                                key={key}
                                maxDataPoint={maxDataPoint}
                                minDataPoint={minDataPoint}
                              />
                            }
                          />
                        );
                      } else return null;
                    })}
                  {showMedian &&
                    medianKeys.map(medianKey => {
                      return (
                        <Line
                          key={medianKey}
                          type="monotone"
                          dataKey={medianKey}
                          stroke={medianColors[medianKey]}
                          strokeWidth={2}
                          fill={medianColors[medianKey]}
                          activeDot={{ r: 6 }}
                          dot={false}
                        />
                      );
                    })}
                  <Tooltip
                    position={{ y: -60 }}
                    content={
                      <CustomTooltip
                        active
                        payload
                        label
                        granularityType
                        occupancyData={showMedian ? medianData : currentData}
                        dataPoints={selectedFilters}
                      />
                    } //error
                    wrapperStyle={{ zIndex: 100000 }}
                  />
                  {/* <Brush
                    data={showMedian ? medianData : currentData}
                    dataKey="date"
                    height={20}
                    stroke={`${COLOR_PRIMARY_LIGHT}80`}
                    travellerStroke={COLOR_PRIMARY}
                    fill="transparent"
                    onChange={e => {
                      changeDataAccordingToBrush(e);
                    }}
                    barGraphData={showMedian ? medianData : averageDataSet}
                    barGraphDataRange={showMedian ? medianDataRange : averageDataRange}
                    barKey={showMedian ? 'Median Available Rate' : 'average'}
                    granularityType={granularityType ? granularityType : null}
                  /> */}
                </ComposedChart>
              </ResponsiveContainer>
            )}

            {/* ===================================================== */}
            {/* ===================================================== */}
            {/* ===================================================== */}

            {title.includes('Active Listing Count') && granularityType === 'Monthly' && (
              <ResponsiveContainer width="100%" height={350} style={{ background: 'yellow' }}>
                <ComposedChart
                  height={300}
                  data={showMedian ? medianData : currentData}
                  margin={{ top: 20, right: 5, bottom: 0, left: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="date"
                    style={{ fontFamily: 'Roboto', fontSize: isMobileScreen ? '10px' : '12px' }}
                    interval={'equidistantPreserveStart'}
                    tickCount={12}
                    padding="gap"
                    tickFormatter={tick => {
                      if (granularityType) {
                        return formatDate(tick, true, true);
                      } else {
                        return formatDate(tick);
                      }
                    }}
                    height={80}
                    // angle={-90}
                    tickMargin={10}
                    textAnchor="end"
                  />

                  <YAxis
                    style={{ fontFamily: 'Roboto', fontSize: '12px' }}
                    tickLine={false}
                    yAxisId="right"
                    orientation="left"
                    axisLine={{ stroke: '#0000' }}
                    tickFormatter={value => abbreviateNumber(value)}
                  />

                  <Bar
                    radius={[5, 5, 0, 0]}
                    data={activeDays}
                    dataKey="active_listings_count"
                    barSize={20}
                    fill={barColor ? barColor : HISTORICAL_BARGRAPH_COLOR}
                    yAxisId="right"
                    height={150}
                    marginTop={-30}
                  />

                  <Tooltip
                    position={{ y: -60 }}
                    content={
                      <CustomTooltip
                        active
                        payload
                        label
                        granularityType
                        dataPoints={selectedFilters}
                      />
                    } //error
                    wrapperStyle={{ zIndex: 100000 }}
                  />
                  {/* <Brush
                    data={showMedian ? medianData : currentData}
                    dataKey="date"
                    height={20}
                    stroke={`${COLOR_PRIMARY_LIGHT}80`}
                    travellerStroke={COLOR_PRIMARY}
                    fill="transparent"
                    onChange={e => {
                      changeDataAccordingToBrush(e);
                    }}
                    barGraphData={showMedian ? medianData : averageDataSet}
                    barGraphDataRange={showMedian ? medianDataRange : averageDataRange}
                    barKey={showMedian ? 'Median Available Rate' : 'average'}
                    granularityType={granularityType ? granularityType : null}
                  />  */}
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

const formatDate = (
  dateString: Date,
  showDay: boolean = false,
  shortYear: boolean = false
): string => {
  const dateStringParsed = dateString.toString();
  let year: number = Number(dateStringParsed.substring(0, 4));
  let month: number = Number(dateStringParsed.substring(5, 7));
  let day: number = 1;

  let tempMonth: number = month - 1; // For 2021-02-25 we will have to pass 2021-01-25
  const date = new Date(year, tempMonth, day); // 2021-01-25 => Feb
  const monthName: string = date.toLocaleString('default', { month: 'short' });

  if (shortYear) {
    year = year % 100;
  }

  const visibleDate: string = showDay ? `${monthName} ${day}, ${year}` : `${monthName} ${year}`;
  return visibleDate;
};

const formatMonthOnlyDate = (monthString: string, shortYear: boolean = false) => {
  const monthYearArray = monthString.split('-');
  let year: number = Number(monthYearArray[0]);
  let month: number = Number(monthYearArray[1]);
  let day = 1;

  let tempMonth: number = month - 1; // For 2021-02-25 we will have to pass 2021-01-25
  const date = new Date(year, tempMonth, day);

  if (shortYear) {
    year = year % 100;
  }

  const monthName: string = date.toLocaleString('default', { month: 'short' });
  return `${monthName}, ${year}`;
};

const getAverageData = (data: RechartsDataFormat[], selectedFilters: string[]) => {
  let averageDataSet: RechartsDataFormat[] = [];
  let averageDataRange = {
    minData: 0,
    maxData: 0,
  };
  data.forEach(dataObj => {
    const { date, ...rest } = dataObj;
    const selectedFiltersObj = Object.keys(rest)
      .filter(key => selectedFilters.includes(key))
      .reduce((obj, key) => {
        obj[key] = rest[key];
        return obj;
      }, {});
    const sum = Object.values(selectedFiltersObj).reduce(
      (a, b) => (a as number) + (b as number),
      0
    );
    const averageObj = { date, average: (sum as number) / selectedFilters.length };
    if (averageObj.average > averageDataRange.maxData)
      averageDataRange.maxData = averageObj.average;
    if (averageObj.average < averageDataRange.minData)
      averageDataRange.minData = averageObj.average;
    averageDataSet.push(averageObj);
  });
  return { averageDataSet, averageDataRange };
};

export const findMaxMin = (data, key, selectedFilters) => {
  let maxDataPoint = -Infinity;
  let minDataPoint = Infinity;

  selectedFilters.sort((a, b) => {
    const numA = parseInt(a.match(/\d+/)?.[0] || 0);
    const numB = parseInt(b.match(/\d+/)?.[0] || 0);

    return numA - numB;
  });

  if (key === 'occupancy_rate') {
    for (const value of data) {
      maxDataPoint = Math.max(maxDataPoint, value['occupancy_rate']);
      minDataPoint = Math.min(minDataPoint, value['occupancy_rate']);
    }
  }

  selectedFilters.forEach(key => {
    for (const value of data) {
      maxDataPoint = Math.max(maxDataPoint, value[key]);
      minDataPoint = Math.min(minDataPoint, value[key]);
    }
  });

  return { maxDataPoint, minDataPoint };
};
export default RateAnalysisCard;
